/**
 * BuilderCertificationsTab Component
 * Låter byggare hantera sina egna behörigheter/certifieringar
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_URL, getAvatarUrl, authHeaders } from '../../../utils/api';
import { CERTIFICATION_OPTIONS, getCertificationInfo } from '../../../constants/certificationTypes';
import { useNotifications } from '../../../contexts/NotificationContext';
import CertifieringsMarke from './CertifieringsMarke';
import RegisterLankFalt from './RegisterLankFalt';
import '../../../css/Modal.css';

// Hjälpfunktion för att fixa UTF-8 encoding
const fixEncoding = (str) => {
  if (!str) return str;
  try {
    if (str.includes('Ã') || str.includes('Ã¤') || str.includes('Ã¶')) {
      const bytes = new Uint8Array([...str].map(c => c.charCodeAt(0)));
      return new TextDecoder('utf-8').decode(bytes);
    }
    return str;
  } catch {
    return str;
  }
};

export const BuilderCertificationsTab = () => {
  const { showSuccess, showError } = useNotifications();
  const [builder, setBuilder] = useState(null);
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [generatingAppendix, setGeneratingAppendix] = useState(false);

  const [newCert, setNewCert] = useState({
    type: '',
    name: '',
    certificateNumber: '',
    expiryDate: '',
    registerUrl: '',
    registerForetag: '',
    registerOrgnr: '',
    file: null
  });
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState(null);

  // Hämta byggföretag för inloggad användare
  const fetchMyBuilder = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/builders/me/builder`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          setError('Inget byggföretag kopplat till ditt konto');
          return;
        }
        throw new Error('Kunde inte hämta byggföretag');
      }

      const data = await response.json();
      setBuilder(data);
      setCertifications(data.certifications || []);
    } catch (err) {
      console.error('Error fetching builder:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMyBuilder();
  }, [fetchMyBuilder]);

  // Lägg till ny behörighet
  const handleAddCertification = () => {
    if (!newCert.type) return;

    const certInfo = getCertificationInfo(newCert.type);
    const file = newCert.file;
    const certification = {
      type: newCert.type,
      name: newCert.type === 'other' ? newCert.name : certInfo.name,
      certificateNumber: newCert.certificateNumber,
      expiryDate: newCert.expiryDate || null,
      documentUrl: file ? URL.createObjectURL(file) : null,
      documentName: file ? file.name : null,
      _pendingFile: file || null,
      registerUrl: newCert.registerUrl.trim() || null,
      registerForetag: newCert.registerForetag.trim() || null,
      registerOrgnr: newCert.registerOrgnr.trim() || null,
      isActive: true,
      addedAt: new Date().toISOString()
    };

    setCertifications([...certifications, certification]);
    setNewCert({ type: '', name: '', certificateNumber: '', expiryDate: '', registerUrl: '', registerForetag: '', registerOrgnr: '', file: null });
    setShowAddForm(false);
    setHasChanges(true);
  };

  // Ta bort behörighet
  const handleRemoveCertification = (index) => {
    const updated = certifications.filter((_, i) => i !== index);
    setCertifications(updated);
    setHasChanges(true);
    setDeleteConfirmIndex(null);
  };

  // Hantera filuppladdning
  const handleFileUpload = (index, file) => {
    if (!file) return;

    const tempUrl = URL.createObjectURL(file);
    const updated = [...certifications];
    updated[index] = {
      ...updated[index],
      documentUrl: tempUrl,
      documentName: file.name,
      _pendingFile: file
    };
    setCertifications(updated);
    setHasChanges(true);
  };

  // Registeruppgifter: företag, org.nr och länk (se RegisterLankFalt). Backend
  // normaliserar länken och kastar allt som inte är http(s).
  const handleRegisterChange = (index, andringar) => {
    const updated = [...certifications];
    updated[index] = { ...updated[index], ...andringar };
    setCertifications(updated);
    setHasChanges(true);
  };

  // Ta bort dokument
  const handleRemoveDocument = (index) => {
    const updated = [...certifications];
    updated[index] = {
      ...updated[index],
      documentUrl: null,
      documentName: null,
      _pendingFile: null
    };
    setCertifications(updated);
    setHasChanges(true);
  };

  // Spara ändringar
  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      // Kolla om vi har filer att ladda upp
      const pendingFiles = certifications
        .map((cert, index) => ({ index, file: cert._pendingFile }))
        .filter(item => item.file instanceof File);

      // Använd alltid FormData för konsistens med multer middleware
      const formData = new FormData();

      // Lägg till certifications som JSON (utan _pendingFile)
      const certsWithoutFiles = certifications.map(cert => {
        const { _pendingFile, ...rest } = cert;
        return rest;
      });
      formData.append('certifications', JSON.stringify(certsWithoutFiles));

      // Lägg till eventuella filer
      pendingFiles.forEach(({ index, file }) => {
        formData.append(`certificationFile_${index}`, file, file.name);
      });

      const response = await fetch(`${API_URL}/builders/me/certifications`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
          // Låt fetch sätta rätt content-type för FormData automatiskt
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Kunde inte spara behörigheter');
      }

      const updatedBuilder = await response.json();
      setBuilder(updatedBuilder);
      setCertifications(updatedBuilder.certifications || []);
      setHasChanges(false);
      showSuccess('Behörigheter sparade', 'Dina behörigheter har uppdaterats.');
    } catch (err) {
      console.error('Error saving certifications:', err);
      showError('Kunde inte spara', err.message || 'Ett fel uppstod vid sparning av behörigheter.');
    } finally {
      setSaving(false);
    }
  };

  // Ladda ner behörighetsbilaga
  const handleDownloadAppendix = async () => {
    if (!builder?._id) return;

    setGeneratingAppendix(true);
    try {
      const response = await fetch(`${API_URL}/builders/${builder._id}/certifications-appendix`, { headers: authHeaders() });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Kunde inte generera bilagan');
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      let fileName = 'Behörighetsbilaga.pdf';
      if (contentDisposition) {
        const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/);
        const filenameMatch = contentDisposition.match(/filename="(.+?)"/);
        if (filenameStarMatch) {
          fileName = decodeURIComponent(filenameStarMatch[1]);
        } else if (filenameMatch) {
          fileName = filenameMatch[1];
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Fel vid generering av bilaga:', error);
      showError('Kunde inte generera bilaga', error.message || 'Ett fel uppstod vid generering av behörighetsbilagan.');
    } finally {
      setGeneratingAppendix(false);
    }
  };

  // Formatera datum
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('sv-SE');
  };

  // Kolla om utgångsdatum har passerat
  const isExpired = (dateString) => {
    if (!dateString) return false;
    return new Date(dateString) < new Date();
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-leadme-pink mb-4"></div>
        <p className="text-gray-500">Laddar behörigheter...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <svg className="w-12 h-12 mx-auto text-red-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  // Registerlänkar räknas — bilagan gör en standardsida av dem
  const hasDocuments = certifications.some(cert => (cert.documentUrl && !cert._pendingFile) || cert.registerUrl);

  return (
    <div className="space-y-6">
      {/* Header med företagsinfo */}
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
        <div className="flex items-center gap-3 sm:gap-4">
          {builder?.avatar ? (
            <img
              src={getAvatarUrl(builder.avatar)}
              alt={builder.companyName}
              className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg bg-gradient-to-br from-leadme-pink to-pink-400 flex items-center justify-center text-white text-lg sm:text-xl font-bold flex-shrink-0">
              {builder?.companyName?.substring(0, 2).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">{builder?.companyName}</h2>
            <p className="text-sm text-gray-500">
              {certifications.length === 0
                ? 'Inga behörigheter registrerade'
                : `${certifications.length} behörighet${certifications.length !== 1 ? 'er' : ''}`
              }
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-3">
          {hasDocuments && (
            <button
              onClick={handleDownloadAppendix}
              disabled={generatingAppendix}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors text-sm"
            >
              {generatingAppendix ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Genererar...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Ladda ner bilaga
                </>
              )}
            </button>
          )}

          {hasChanges && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors text-sm"
            >
              {saving ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sparar...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Spara
                </>
              )}
            </button>
          )}

          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-leadme-pink text-white rounded-lg hover:bg-leadme-pink-dark transition-colors text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Lägg till
          </button>
        </div>
      </div>

      {/* Lägg till-modal */}
      {showAddForm && (
        <div className="modal-overlay" onClick={() => { setShowAddForm(false); setNewCert({ type: '', name: '', certificateNumber: '', expiryDate: '', registerUrl: '', registerForetag: '', registerOrgnr: '', file: null }); }}>
          <div className="modal-content-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-scroll-container">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Lägg till behörighet</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Typ av behörighet *</label>
                  <select
                    value={newCert.type}
                    onChange={(e) => setNewCert({ ...newCert, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-leadme-pink focus:border-leadme-pink"
                  >
                    <option value="">Välj typ...</option>
                    {CERTIFICATION_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {newCert.type === 'other' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Namn på behörighet *</label>
                    <input
                      type="text"
                      value={newCert.name}
                      onChange={(e) => setNewCert({ ...newCert, name: e.target.value })}
                      placeholder="T.ex. Heta arbeten"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-leadme-pink focus:border-leadme-pink"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Certifikatnummer</label>
                  <input
                    type="text"
                    value={newCert.certificateNumber}
                    onChange={(e) => setNewCert({ ...newCert, certificateNumber: e.target.value })}
                    placeholder="Valfritt"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-leadme-pink focus:border-leadme-pink"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Utgångsdatum</label>
                  <input
                    type="date"
                    value={newCert.expiryDate}
                    onChange={(e) => setNewCert({ ...newCert, expiryDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-leadme-pink focus:border-leadme-pink"
                  />
                </div>
              </div>

              {/* Registeruppgifter */}
              {newCert.type && (
                <div className="mt-4">
                  <RegisterLankFalt
                    key={newCert.type}
                    type={newCert.type}
                    value={newCert}
                    onChange={(andringar) => setNewCert(prev => ({ ...prev, ...andringar }))}
                  />
                </div>
              )}

              {/* Dokument */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Dokument (valfritt)</label>
                {newCert.file ? (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                      </svg>
                      <span className="truncate max-w-[200px]">{newCert.file.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setNewCert({ ...newCert, file: null })}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <label className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer text-sm text-gray-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    Välj fil...
                    <input
                      type="file"
                      accept=".pdf,image/*"
                      onChange={(e) => setNewCert({ ...newCert, file: e.target.files[0] || null })}
                      className="hidden"
                    />
                  </label>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewCert({ type: '', name: '', certificateNumber: '', expiryDate: '', registerUrl: '', registerForetag: '', registerOrgnr: '', file: null });
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Avbryt
                </button>
                <button
                  onClick={handleAddCertification}
                  disabled={!newCert.type || (newCert.type === 'other' && !newCert.name)}
                  className="px-4 py-2 bg-leadme-pink text-white rounded-lg hover:bg-leadme-pink-dark disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Lägg till
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lista med behörigheter */}
      {certifications.length > 0 ? (
        <div className="bg-white rounded-lg shadow-sm divide-y divide-gray-100">
          {certifications.map((cert, index) => {
            const certInfo = getCertificationInfo(cert.type);
            const expired = isExpired(cert.expiryDate);
            const displayName = fixEncoding(cert.name) || certInfo.name;
            const documentName = fixEncoding(cert.documentName) || 'Dokument';
            const isExpanded = expandedIndex === index;

            return (
              <div key={index} className={`${expired ? 'bg-red-50' : ''}`}>
                <div
                  className="p-4 flex items-center gap-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedIndex(isExpanded ? null : index)}
                >
                  <CertifieringsMarke type={cert.type} size="lg" />

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-gray-900">{displayName}</h4>
                      {expired && (
                        <span className="text-xs text-red-600 bg-red-100 px-2 py-0.5 rounded">Utgången</span>
                      )}
                      {cert._pendingFile && (
                        <span className="text-xs text-yellow-600 bg-yellow-100 px-2 py-0.5 rounded">Ej sparad</span>
                      )}
                    </div>
                    <div className="flex flex-col gap-0.5 mt-1 text-xs text-gray-500">
                      {cert.certificateNumber && <span>Nr: {cert.certificateNumber}</span>}
                      {cert.expiryDate && (
                        <span className={expired ? 'text-red-600' : ''}>
                          Utgår: {formatDate(cert.expiryDate)}
                        </span>
                      )}
                      {cert.documentUrl && <span className="text-green-600">Dokument bifogat</span>}
                      {cert.registerUrl && <span className="text-blue-600">Registerlänk tillagd</span>}
                    </div>
                  </div>

                  {/* Expand-ikon */}
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>

                {/* Expanderad sektion */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-2 bg-gray-50 border-t space-y-3">
                    {/* Registeruppgifter */}
                    <RegisterLankFalt
                      type={cert.type}
                      value={cert}
                      onChange={(andringar) => handleRegisterChange(index, andringar)}
                    />

                    {/* Dokument-rad */}
                    <div>
                      {cert.documentUrl ? (
                        <div className="flex items-center gap-2">
                          <a
                            href={cert.documentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-3 py-2 bg-white border rounded-lg hover:bg-gray-50 text-sm"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                            </svg>
                            {documentName}
                          </a>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveDocument(index);
                            }}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                            title="Ta bort dokument"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <label className="inline-flex items-center gap-2 px-3 py-2 bg-white border rounded-lg hover:bg-gray-50 cursor-pointer text-sm">
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                          </svg>
                          Ladda upp dokument
                          <input
                            type="file"
                            accept=".pdf,image/*"
                            onChange={(e) => {
                              e.stopPropagation();
                              handleFileUpload(index, e.target.files[0]);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>

                    {/* Ta bort behörighet — egen rad */}
                    <div className="pt-2 border-t border-gray-200">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirmIndex(index);
                        }}
                        className="flex items-center gap-1.5 px-2 py-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded text-xs"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Ta bort behörighet
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Inga behörigheter registrerade</h3>
          <p className="text-gray-500 mb-4">Lägg till era certifieringar och behörigheter för att visa dem för kunder.</p>
          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-leadme-pink text-white rounded-lg hover:bg-leadme-pink-dark"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Lägg till första behörigheten
          </button>
        </div>
      )}

      {/* Bekräftelse-modal för borttagning */}
      {deleteConfirmIndex !== null && (
        <div className="modal-overlay" onClick={() => setDeleteConfirmIndex(null)}>
          <div className="modal-content-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-scroll-container">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Bekräfta borttagning
              </h3>
              <p className="text-gray-600 mb-2">
                Är du säker på att du vill ta bort denna behörighet?
              </p>
              <p className="text-sm font-medium text-gray-900 mb-6">
                {certifications[deleteConfirmIndex]?.name || 'Okänd behörighet'}
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeleteConfirmIndex(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Avbryt
                </button>
                <button
                  onClick={() => handleRemoveCertification(deleteConfirmIndex)}
                  className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Ta bort
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuilderCertificationsTab;
